/**
 * Edit-session panel for sessions.
 */
import * as React from 'react';
import { isTerminalStatus } from '@/components/sessions/remote/session-form-utils';
import { useSession } from '@/hooks/useSessionsRemote';
import { useUnitGetNotFound } from '@/hooks/useUnitGetNotFound';
import { getTabForSessionType } from '@/utils/common-utils';
import { FieldEventForm } from './session-form-builder/FieldEventForm';
import { InformationMeetingForm } from './session-form-builder/InformationMeetingForm';
import { TutoringSessionForm } from './session-form-builder/TutoringSessionForm';
import { WelcomeMeetingForm } from './session-form-builder/WelcomeMeetingForm';
import { Spinner } from '@/components/ui/spinner';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link EditSessionView}. */
interface EditSessionViewProps {
  sessionId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Shown while GET session is in flight. */
const LoadingState = () => (
  <div className="form-panel-loading">
    <Spinner size="lg" />
  </div>
);

/** Missing or unknown session — close returns to the list. */
const NotFoundState = ({ onClose }: { onClose: () => void }) => (
  <div className="p-8 text-center">
    <p className="text-red-600">{getErrorMessage('sessionNotFound')}</p>
    <button onClick={onClose} className="mt-4 text-blue-600 hover:underline">
      Fermer
    </button>
  </div>
);

/** Terminal statuses cannot be patched — show a read-only warning instead of the form. */
const TerminalStatusState = ({
  session,
  onClose,
}: {
  session: { status: string };
  onClose: () => void;
}) => (
  <div className="p-8">
    <div
      className="rounded-lg p-4 border"
      style={{
        background: 'color-mix(in srgb, rgb(var(--status-warning)) 10%, transparent)',
        borderColor: 'rgb(var(--status-warning))',
      }}
    >
      <h3 className="font-semibold mb-2">{getErrorMessage('sessionTerminalNoEdit')}</h3>
      <p className="text-sm">
        {getErrorMessage({ code: 'sessionTerminalNoEditWithStatus', status: session.status })}
      </p>
      <button onClick={onClose} className="mt-4 text-blue-600 hover:underline">
        Fermer
      </button>
    </div>
  </div>
);

/** Edit-session panel for sessions. */
export const EditSessionView: React.FC<EditSessionViewProps> = ({
  sessionId,
  onClose,
  onSuccess,
}) => {
  const { data: session, isLoading, isError, error } = useSession(sessionId);
  useUnitGetNotFound({
    isError,
    error,
    catalogKey: 'sessionNotFound',
    onClose,
    entityId: sessionId,
  });

  if (isLoading) return <LoadingState />;
  if (!session) return <NotFoundState onClose={onClose} />;
  if (isTerminalStatus(session.status)) {
    return <TerminalStatusState session={session} onClose={onClose} />;
  }

  const tab = getTabForSessionType(session.type);
  const shared = { mode: 'edit' as const, session, sessionId, onClose, onSuccess };

  if (tab === 'tutoring') {
    return <TutoringSessionForm {...shared} />;
  }
  if (tab === 'information-meeting') {
    return <InformationMeetingForm {...shared} />;
  }
  if (tab === 'welcome-meeting') {
    return <WelcomeMeetingForm {...shared} />;
  }
  if (tab === 'field-event') {
    return <FieldEventForm {...shared} />;
  }

  return <NotFoundState onClose={onClose} />;
};
